import { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  TouchableOpacity,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { Clock, Info } from 'lucide-react-native';
import type { Duel } from './DuelCard';
import { useUnits } from '@/lib/useUnits';
import { resolveDuelExerciseNames } from '@/lib/exerciseCatalog';
import DuelScoreComparison from './DuelScoreComparison';
import DuelLiftFeed from './DuelLiftFeed';
import { GRIND_MS, GRIND_EASING, SNAP_MS } from '@/lib/animations';

const ANIMATION_CONSTANTS = {
  SPARK_OPACITY_MAX: 0.8,
  SPARK_FLICKER_DURATION: 100,
};

const DUEL_QUOTES = [
  '"The iron never lies." - Henry Rollins',
  '"Strength does not come from winning." - Mahatma Gandhi',
  '"The last three or four reps is what makes the muscle grow." - Arnold',
  '"No citizen has a right to be an amateur in physical training."',
  '"The only bad workout is the one that didn\'t happen."',
  '"Your opponent is warming up right now."',
  '"You don\'t find willpower - you create it."',
  '"Train insane or remain the same."',
  '"Be stronger than your excuses."',
  '"Pain is temporary. Pride is forever."',
  '"Iron sharpens iron."',
  '"The weight doesn\'t care about your feelings."',
];

interface LiveDuelProgressProps {
  duel: Duel;
  currentUserId: string;
}

interface ProfileInfo {
  name: string;
  avatarUrl: string | null;
}

interface ActivityEntry {
  exercise_name: string;
  performed_at: string;
  exercise_count: number;
  user_id: string;
}

export default function LiveDuelProgress({ duel, currentUserId }: LiveDuelProgressProps) {
  const { theme } = useTheme();
  const { formatWeight } = useUnits();
  const [challengerBest, setChallengerBest] = useState<number | null>(null);
  const [challengedBest, setChallengedBest] = useState<number | null>(null);
  const [challengerInfo, setChallengerInfo] = useState<ProfileInfo>({ name: 'You', avatarUrl: null });
  const [challengedInfo, setChallengedInfo] = useState<ProfileInfo>({ name: 'Opponent', avatarUrl: null });
  const [timeLeft, setTimeLeft] = useState('');
  const [loading, setLoading] = useState(true);
  const [showHowToWin, setShowHowToWin] = useState(false);
  const [activities, setActivities] = useState<ActivityEntry[]>([]);

  const tugAnim = useRef(new Animated.Value(0.5)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const clashShake = useRef(new Animated.Value(0)).current;
  const cardScale = useRef(new Animated.Value(0.95)).current;
  const cardOpacity = useRef(new Animated.Value(0)).current;
  const borderGlow = useRef(new Animated.Value(0)).current;
  const energyFlow = useRef(new Animated.Value(0)).current;
  const energyFlow2 = useRef(new Animated.Value(0)).current;

  // New 90s gritty animations
  const grindingSpark = useRef(new Animated.Value(0)).current;
  const screenShake = useRef(new Animated.Value(0)).current;
  const challengerScoreStamp = useRef(new Animated.Value(1)).current;
  const challengedScoreStamp = useRef(new Animated.Value(1)).current;
  const prevChallengerScore = useRef(challengerBest);
  const prevChallengedScore = useRef(challengedBest);
  const isFirstLoad = useRef(true);

  // Fixed duel colors (theme-independent)
  const DUEL_BLUE = '#3B82F6';
  const DUEL_RED = '#EF4444';

  const isChallenger = duel.challenger_id === currentUserId;
  const opponentId = isChallenger ? duel.challenged_id : duel.challenger_id;

  // Pressure pulse - the stronger side pushes, then the weaker side resists back
  // Direction based on who's winning: positive = challenger pushing right, negative = challenged pushing left
  const winnerPush = (challengerBest ?? 0) >= (challengedBest ?? 0) ? 1 : -1;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        // Strong push from winner
        Animated.timing(clashShake, { toValue: winnerPush * 1, duration: 600, useNativeDriver: true }),
        // Brief hold - the force lands
        Animated.delay(200),
        // Weaker side pushes back, but not as far
        Animated.timing(clashShake, { toValue: winnerPush * -0.3, duration: 800, useNativeDriver: true }),
        // Settle to resting pressure
        Animated.timing(clashShake, { toValue: winnerPush * 0.15, duration: 400, useNativeDriver: true }),
        // Pause - building tension for next push
        Animated.delay(1500),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [winnerPush]);

  // Card entrance animation
  useEffect(() => {
    Animated.parallel([
      Animated.spring(cardScale, { toValue: 1, friction: 6, tension: 80, useNativeDriver: true }),
      Animated.timing(cardOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
    ]).start();
  }, []);

  // Border glow pulse - breathing effect
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(borderGlow, { toValue: 1, duration: 4000, useNativeDriver: false }),
        Animated.timing(borderGlow, { toValue: 0, duration: 4000, useNativeDriver: false }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, []);

  // Weight pressure animation - slow, grinding force
  useEffect(() => {
    const loop1 = Animated.loop(
      Animated.timing(energyFlow, { toValue: 1, duration: 2500, useNativeDriver: false }),
    );
    const timer = setTimeout(() => {
      const loop2 = Animated.loop(
        Animated.timing(energyFlow2, { toValue: 1, duration: 2500, useNativeDriver: false }),
      );
      loop2.start();
    }, 1250);
    loop1.start();
    return () => { loop1.stop(); clearTimeout(timer); };
  }, []);

  // HEARTBEAT timer pulse - sharp pulse, long rest (heavy feel)
  useEffect(() => {
    const ms = duel.end_at ? Math.max(0, new Date(duel.end_at).getTime() - Date.now()) : Infinity;
    if (ms > 0 && ms < 3600000) {
      const loop = Animated.loop(
        Animated.sequence([
          // Sharp heartbeat pulse up
          Animated.timing(pulseAnim, {
            toValue: 1.05,
            duration: 100,
            useNativeDriver: false
          }),
          // Back to normal over 900ms
          Animated.timing(pulseAnim, {
            toValue: 1.0,
            duration: 900,
            useNativeDriver: false
          }),
        ])
      );
      loop.start();
      return () => loop.stop();
    } else {
      pulseAnim.setValue(1);
    }
  }, [timeLeft]);

  useEffect(() => {
    loadProfiles();
    loadScores();
    loadActivity();
    const interval = setInterval(() => { loadScores(); loadActivity(); }, 30000);
    return () => clearInterval(interval);
  }, [duel.id]);

  useEffect(() => {
    const tick = () => {
      if (!duel.end_at) { setTimeLeft('--:--:--'); return; }
      const ms = Math.max(0, new Date(duel.end_at).getTime() - Date.now());
      if (ms <= 0) { setTimeLeft('0:00:00'); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setTimeLeft(`${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [duel.end_at]);

  const loadProfiles = async () => {
    const ids = [duel.challenger_id, duel.challenged_id].filter(Boolean);
    const { data } = await supabase
      .from('profiles')
      .select('id, display_name, handle, avatar_url')
      .in('id', ids);
    if (data) {
      for (const p of data) {
        const name = p.id === currentUserId ? 'You' : (p.display_name || p.handle || 'Lifter');
        const info: ProfileInfo = { name, avatarUrl: p.avatar_url ?? null };
        if (p.id === duel.challenger_id) setChallengerInfo(info);
        if (p.id === duel.challenged_id) setChallengedInfo(info);
      }
    }
  };

  const loadScores = async () => {
    try {
      const { data, error } = await supabase.rpc('get_duel_scores', { p_duel_id: duel.id });
      if (error || !data) throw error;
      const scores = typeof data === 'string' ? JSON.parse(data) : data;

      const cScore = scores.challenger_score ?? 0;
      const dScore = scores.challenged_score ?? 0;

      // Check for score changes to trigger heavy animations
      const challengerChanged = prevChallengerScore.current !== (cScore > 0 ? cScore : null);
      const challengedChanged = prevChallengedScore.current !== (dScore > 0 ? dScore : null);
      const anyScoreChanged = challengerChanged || challengedChanged;

      setChallengerBest(cScore > 0 ? cScore : null);
      setChallengedBest(dScore > 0 ? dScore : null);

      // Update refs for next comparison
      prevChallengerScore.current = cScore > 0 ? cScore : null;
      prevChallengedScore.current = dScore > 0 ? dScore : null;

      // Heavy grinding tug-of-war animation
      const total = cScore + dScore;
      const ratio = total === 0 ? 0.5 : cScore / total;

      if (isFirstLoad.current) {
        // DRAMATIC ENTRANCE: bar slams right, grinds back left, overshoots, then settles
        isFirstLoad.current = false;
        Animated.sequence([
          // Slam hard to the right (challenger pushes)
          Animated.timing(tugAnim, {
            toValue: 0.75,
            duration: 600,
            easing: GRIND_EASING,
            useNativeDriver: false,
          }),
          // Resistance - grinds back past center to the left
          Animated.timing(tugAnim, {
            toValue: 0.3,
            duration: 900,
            easing: GRIND_EASING,
            useNativeDriver: false,
          }),
          // Overshoot back right, smaller this time
          Animated.timing(tugAnim, {
            toValue: ratio + (ratio >= 0.5 ? 0.06 : -0.06),
            duration: 700,
            easing: GRIND_EASING,
            useNativeDriver: false,
          }),
          // Finally settle into actual position - heavy, grinding
          Animated.timing(tugAnim, {
            toValue: ratio,
            duration: 500,
            easing: GRIND_EASING,
            useNativeDriver: false,
          }),
        ]).start();
      } else {
        // Normal update: GRIND to new position
        Animated.timing(tugAnim, {
          toValue: ratio,
          duration: GRIND_MS,
          easing: GRIND_EASING,
          useNativeDriver: false,
        }).start();
      }

      // When a new lift is logged, trigger screen shake and spark effects
      if (anyScoreChanged) {
        // Screen shake - 2px horizontal shake, 150ms oscillation
        Animated.sequence([
          Animated.timing(screenShake, {
            toValue: 2,
            duration: 37.5, // 150ms / 4
            useNativeDriver: false,
          }),
          Animated.timing(screenShake, {
            toValue: -2,
            duration: 75, // 150ms / 2
            useNativeDriver: false,
          }),
          Animated.timing(screenShake, {
            toValue: 0,
            duration: 37.5, // 150ms / 4
            useNativeDriver: false,
          }),
        ]).start();

        // Grinding spark effect at collision seam
        Animated.sequence([
          Animated.timing(grindingSpark, {
            toValue: ANIMATION_CONSTANTS.SPARK_OPACITY_MAX,
            duration: ANIMATION_CONSTANTS.SPARK_FLICKER_DURATION,
            useNativeDriver: false,
          }),
          Animated.timing(grindingSpark, {
            toValue: 0,
            duration: ANIMATION_CONSTANTS.SPARK_FLICKER_DURATION,
            useNativeDriver: false,
          }),
          Animated.timing(grindingSpark, {
            toValue: ANIMATION_CONSTANTS.SPARK_OPACITY_MAX * 0.6,
            duration: ANIMATION_CONSTANTS.SPARK_FLICKER_DURATION,
            useNativeDriver: false,
          }),
          Animated.timing(grindingSpark, {
            toValue: 0,
            duration: ANIMATION_CONSTANTS.SPARK_FLICKER_DURATION,
            useNativeDriver: false,
          }),
        ]).start();

        // Score FLASH animations - scale 1.4 → 1.0 with spring tension 250
        if (challengerChanged) {
          Animated.sequence([
            Animated.spring(challengerScoreStamp, {
              toValue: 1.4, // Flash bigger for more impact
              tension: 250,
              friction: 10,
              useNativeDriver: false,
            }),
            Animated.spring(challengerScoreStamp, {
              toValue: 1,
              tension: 250,
              friction: 10,
              useNativeDriver: false,
            }),
          ]).start();
        }

        if (challengedChanged) {
          Animated.sequence([
            Animated.spring(challengedScoreStamp, {
              toValue: 1.4, // Flash bigger for more impact
              tension: 250,
              friction: 10,
              useNativeDriver: false,
            }),
            Animated.spring(challengedScoreStamp, {
              toValue: 1,
              tension: 180,
              friction: 8,
              useNativeDriver: false,
            }),
          ]).start();
        }
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async () => {
    try {
      const since = duel.start_at || duel.created_at;
      const until = duel.end_at || new Date().toISOString();
      const ids = [duel.challenger_id, duel.challenged_id].filter(Boolean);
      const exerciseNames = resolveDuelExerciseNames(duel.exercise_name || duel.exercise_id);
      const nameSet = exerciseNames ? new Set(exerciseNames.map(n => n.toLowerCase())) : null;

      const { data } = await supabase
        .from('workouts')
        .select('user_id, performed_at, payload')
        .in('user_id', ids)
        .gte('performed_at', since)
        .lte('performed_at', until)
        .order('performed_at', { ascending: false })
        .limit(10);

      if (!data) { setActivities([]); return; }

      // Push notification handled by notifyOpponentLoggedLift() in workoutSave.ts
      const mapped: ActivityEntry[] = [];
      for (const w of data) {
        let payload = w.payload;
        if (typeof payload === 'string') {
          try { payload = JSON.parse(payload); } catch { payload = {}; }
        }
        const exercises: any[] = payload?.exercises ?? [];
        // Filter exercises to those matching the duel's body part/exercise
        const relevant = nameSet
          ? exercises.filter((e: any) => nameSet.has((e.name || '').toLowerCase()))
          : exercises;
        if (relevant.length === 0) continue;
        mapped.push({
          exercise_name: relevant[0]?.name || 'a workout',
          performed_at: w.performed_at,
          exercise_count: relevant.length,
          user_id: w.user_id,
        });
      }
      setActivities(mapped);
    } catch {
      // silent
    }
  };

  const isExpired = duel.end_at ? new Date(duel.end_at).getTime() <= Date.now() : false;

  // Check if opponent has recent activity (within 5 min) for 🔥
  const opponentHasRecentActivity = activities.some(
    a => a.user_id === opponentId && (Date.now() - new Date(a.performed_at).getTime()) < 300000
  );

  const getHowToWinSummary = (): string => {
    switch (duel.duel_type) {
      case '1rm': return 'Highest estimated 1RM wins';
      case 'consistency': return 'Most workout sessions wins';
      case 'pr': return 'Heaviest single-set weight wins';
      case 'strength': return 'Heaviest single set wins';
      default: return 'Highest total volume wins';
    }
  };

  const getHowToWinFull = (): string => {
    const ex = duel.exercise_name || 'lift';
    const h = duel.timeframe_hours;
    switch (duel.duel_type) {
      case '1rm': return `Log your heaviest single-rep ${ex}. The highest estimated 1RM wins.`;
      case 'consistency': return `Log as many workouts as you can within ${h}h. Most sessions wins.`;
      case 'pr': return `Hit the heaviest ${ex} you can. Best single-set weight wins.`;
      case 'strength': return `Log your heaviest ${ex}. Highest weight for a single set wins within ${h}h.`;
      default: return `Log your best ${ex} sets - total volume (weight × reps across all sets) is compared. Highest total volume within ${h}h wins.`;
    }
  };

  const challengerIsYou = duel.challenger_id === currentUserId;
  const challengedIsYou = duel.challenged_id === currentUserId;

  // Score labels for tug-of-war ends
  const leftScore = challengerBest != null ? formatWeight(challengerBest, 'lbs') : '0';
  const rightScore = challengedBest != null ? formatWeight(challengedBest, 'lbs') : '0';

  // Dynamic border color - iron warming under pressure
  const glowBorderColor = borderGlow.interpolate({
    inputRange: [0, 1],
    outputRange: ['#374151', '#6B7280'],
  });

  return (
    <Animated.View style={[styles.container, {
      backgroundColor: '#0A0A0A',
      borderColor: glowBorderColor,
      transform: [
        { scale: cardScale },
        { translateX: screenShake }
      ],
      opacity: cardOpacity,
    }]}>
      {/* === TOP SCAR LINE === */}
      <View style={styles.scarLine} />

      {/* Time remaining - industrial ticker */}
      <View style={styles.timeRow}>
        <Clock size={14} color={isExpired ? '#ef4444' : '#B8972E'} />
        <Animated.Text style={[styles.timeText, {
          color: isExpired ? '#ef4444' : '#B8972E',
          opacity: isExpired ? 1 : pulseAnim,
          transform: [{ scale: isExpired ? 1 : pulseAnim }]
        }]}>
          {isExpired ? 'TIME - EXPIRED' : `${timeLeft} REMAINING`}
        </Animated.Text>
      </View>

      {/* Athletes - bold industrial layout */}
      <DuelScoreComparison
        challengerInfo={challengerInfo}
        challengedInfo={challengedInfo}
        challengerIsYou={challengerIsYou}
        challengedIsYou={challengedIsYou}
        challengerBest={challengerBest}
        challengedBest={challengedBest}
        challengerScoreStamp={challengerScoreStamp}
        challengedScoreStamp={challengedScoreStamp}
        opponentHasRecentActivity={opponentHasRecentActivity}
        isExpired={isExpired}
        formatWeight={formatWeight}
        theme={theme}
      />

      {/* ====== IRON TUG BAR ====== */}
      <View style={styles.tugSection}>
        {/* Score labels above bar */}
        <View style={styles.barLabels}>
          <Text style={styles.barLabelLeft}>{leftScore}</Text>
          <Text style={styles.barLabelRight}>{rightScore}</Text>
        </View>
        {/* The bar - industrial iron split */}
        <View style={styles.barTrack}>
          <Animated.View style={[styles.barYou, {
            width: tugAnim.interpolate({ inputRange: [0, 1], outputRange: ['2%', '98%'] }),
          }]} />
          {/* Heavy divider - iron seam */}
          <Animated.View style={[styles.barDivider, {
            left: tugAnim.interpolate({ inputRange: [0, 1], outputRange: ['2%', '98%'] }),
          }]} />
          {/* Grinding spark at collision */}
          <Animated.View style={[styles.grindingSpark, {
            left: tugAnim.interpolate({ inputRange: [0, 1], outputRange: ['2%', '98%'] }),
            opacity: grindingSpark,
          }]} />
        </View>

        {/* Leading indicator */}
        {(challengerBest ?? 0) !== (challengedBest ?? 0) && (
          <Text style={styles.leadText}>
            {(() => {
              const diff = Math.abs((challengerBest ?? 0) - (challengedBest ?? 0));
              const diffLabel = formatWeight(diff, 'lbs');
              const challengerLeads = (challengerBest ?? 0) > (challengedBest ?? 0);
              if (challengerLeads) {
                return challengerIsYou ? `YOU LEAD BY ${diffLabel}` : `${challengerInfo.name.toUpperCase()} LEADS BY ${diffLabel}`;
              }
              return challengedIsYou ? `YOU LEAD BY ${diffLabel}` : `${challengedInfo.name.toUpperCase()} LEADS BY ${diffLabel}`;
            })()}
          </Text>
        )}
      </View>

      {/* Divider scar */}
      <View style={styles.scarLine} />

      {/* How to Win - condensed */}
      <View style={styles.howToWinRow}>
        <Text style={styles.howToWinSummary}>
          {getHowToWinSummary().toUpperCase()}
        </Text>
        <TouchableOpacity onPress={() => setShowHowToWin(!showHowToWin)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Info size={14} color="#4A4840" />
        </TouchableOpacity>
      </View>
      {showHowToWin && (
        <Text style={styles.howToWinFull}>
          {getHowToWinFull()}
        </Text>
      )}

      {/* Compact activity feed */}
      <DuelLiftFeed
        activities={activities}
        currentUserId={currentUserId}
        challengerId={duel.challenger_id}
        challengerName={challengerInfo.name}
        challengedName={challengedInfo.name}
      />

      {/* Motivational quote */}
      <Text style={styles.quoteText}>
        {DUEL_QUOTES[Math.abs(duel.id.charCodeAt(0) + duel.id.charCodeAt(1)) % DUEL_QUOTES.length]}
      </Text>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 2,
    borderRadius: 2,
    padding: 16,
    gap: 14,
    // Industrial hard shadow
    shadowColor: '#000',
    shadowOpacity: 0.6,
    shadowRadius: 0,
    shadowOffset: { width: 3, height: 3 },
  },
  scarLine: {
    height: 1,
    backgroundColor: '#1A1A1A',
  },
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  timeText: {
    fontSize: 13,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 2,
    textTransform: 'uppercase' as const,
  },

  tugSection: {
    gap: 6,
    position: 'relative' as const,
    marginBottom: 4,
  },
  barLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 2,
  },
  barLabelLeft: {
    fontSize: 11,
    fontFamily: 'Oswald_700Bold',
    color: '#3B82F6',
    letterSpacing: 0.5,
  },
  barLabelRight: {
    fontSize: 11,
    fontFamily: 'Oswald_700Bold',
    color: '#EF4444',
    letterSpacing: 0.5,
  },
  barTrack: {
    height: 14,
    backgroundColor: '#EF4444',
    borderRadius: 1,
    overflow: 'visible',
    position: 'relative' as const,
    borderWidth: 1,
    borderColor: '#1A1A1A',
  },
  barYou: {
    position: 'absolute' as const,
    left: 0, top: 0,
    height: 12,
    backgroundColor: '#3B82F6',
    borderRadius: 0,
  },
  barDivider: {
    position: 'absolute' as const,
    top: -5,
    width: 4,
    height: 22,
    backgroundColor: '#E8E4DB',
    borderRadius: 0,
    marginLeft: -2,
    borderWidth: 1,
    borderColor: '#0A0A0A',
  },
  grindingSpark: {
    position: 'absolute' as const,
    top: -3,
    width: 8,
    height: 18,
    backgroundColor: '#B8972E',
    borderRadius: 0,
    marginLeft: -4,
    shadowColor: '#B8972E',
    shadowOpacity: 0.9,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 0 },
    elevation: 6,
  },
  leadText: {
    fontSize: 11,
    fontFamily: 'BarlowCondensed_700Bold',
    color: '#B8972E',
    textAlign: 'center',
    letterSpacing: 1.5,
    textTransform: 'uppercase' as const,
    marginTop: 2,
  },
  howToWinRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  howToWinSummary: {
    fontSize: 11,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 1,
    color: '#7A776E',
  },
  howToWinFull: {
    fontSize: 12,
    fontFamily: 'Inter_400Regular',
    lineHeight: 18,
    textAlign: 'center',
    paddingHorizontal: 8,
    color: '#4A4840',
  },

  quoteText: {
    fontSize: 11,
    fontStyle: 'italic',
    fontFamily: 'Inter_400Regular',
    textAlign: 'center',
    paddingHorizontal: 8,
    color: '#4A4840',
    borderTopWidth: 1,
    borderTopColor: '#1A1A1A',
    paddingTop: 10,
  },
});
